import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { useContentProtection } from '../../hooks/useContentProtection';
import { 
  Award,
  CheckCircle2,
  Target,
  ArrowLeft,
  ExternalLink,
  BookOpen,
  Keyboard
} from 'lucide-react';
import { CertificateView } from '../../components/certificate/CertificateView';
import { rpatFoundationCertificate } from '../../components/certificate/certificateContent';

const RPATCertificate = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  
  // Apply content protection
  useContentProtection(true);
  
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [generating, setGenerating] = useState(false);
  

  const fetchEligibility = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/rpat/certificates/eligibility');
      setEligibility(res.data);
      
      if (res.data.already_certified) {
        const certRes = await api.get('/rpat/certificates/my-certificate');
        setCertificate(certRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch eligibility:', err);
      toast.error('Failed to load certificate status');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchEligibility();
  }, [fetchEligibility]);

  const generateCertificate = async () => {
    try {
      setGenerating(true);
      const res = await api.post('/rpat/certificates/generate');
      setCertificate(res.data.certificate);
      setEligibility(prev => ({ ...prev, already_certified: true }));
      toast.success('Certificate generated successfully!');
    } catch (err) {
      console.error('Failed to generate certificate:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate certificate');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-96" />
        </div>
      </MainLayout>
    );
  }

  // Certificate View
  if (certificate) {
    return (
      <MainLayout>
        <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="rpat-foundation-certificate-page">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/rpat')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Your Certificate</h1>
              <p className="text-muted-foreground">TekWissen Certified Recruiter - Phase 1-4 (Foundation)</p>
            </div>
          </div>

          {/* Phase 1-4 Certificate Info Banner */}
          <div className="p-4 rounded-lg bg-cyan-50 dark:bg-cyan-950/30 border border-cyan-200 dark:border-cyan-800">
            <div className="flex items-start gap-3">
              <Award className="h-5 w-5 text-cyan-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-cyan-800 dark:text-cyan-300">R-PAT Foundation Certificate (Phase 1-4)</p>
                <p className="text-sm text-cyan-700 dark:text-cyan-400 mt-1">
                  This certificate recognizes completion of the R-PAT foundation training. 
                  For the full TekWissen Certified Recruiter credential with specialization, 
                  complete Phases 5-8 in the Advanced Certification track.
                </p>
              </div>
            </div>
          </div>

          <CertificateView data={rpatFoundationCertificate({ ...certificate, user_email: certificate.user_email || user?.email })} />

          {/* Verification Link */}
          <Card className="bg-muted/30">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <ExternalLink className="h-6 w-6 text-cyan-600" />
                <div className="flex-1">
                  <p className="font-medium">Share Your Achievement</p>
                  <p className="text-sm text-muted-foreground">
                    Anyone can verify your certificate using the credential ID
                  </p>
                </div>
                <Button 
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/rpat/verify/${certificate.credential_id}`);
                    toast.success('Verification link copied!');
                  }}
                >
                  Copy Link
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  // Eligibility View
  return (
    <MainLayout>
      <div className="p-6 max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/rpat')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Foundation Certification</h1>
            <p className="text-muted-foreground">R-PAT Phase 1-4 Certificate</p>
          </div>
        </div>

        {/* Certificate Type Clarification */}
        <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-3">
            <Award className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-blue-800 dark:text-blue-300">Two Certificate Levels Available</p>
              <div className="text-sm text-blue-700 dark:text-blue-400 mt-2 space-y-2">
                <p><strong>1. Foundation (Phase 1-4):</strong> Complete R-PAT core training - You&apos;re here now</p>
                <p><strong>2. Advanced (Phase 5-8):</strong> Complete Job Mastery, Sourcing Labs, Role Play &amp; Final Assessment for the full TekWissen Certified Recruiter credential with specialization</p>
              </div>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader className="text-center">
            <Award className="h-16 w-16 mx-auto text-cyan-600 mb-4" />
            <CardTitle className="text-2xl">Foundation Certificate Requirements</CardTitle>
            <p className="text-sm text-muted-foreground mt-2">Phase 1-4 Completion</p>
          </CardHeader>
          <CardContent>
            {eligibility?.eligible ? (
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full mb-6">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-medium">You meet all requirements!</span>
                </div>
                
                <p className="text-muted-foreground mb-6">
                  Congratulations! You have completed all topics, passed the final exam with 85%+, 
                  and passed all typing tests. Click below to generate your <strong>Foundation Certificate</strong>.
                </p>
                
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-cyan-600 to-blue-600"
                  onClick={generateCertificate}
                  disabled={generating}
                >
                  {generating ? 'Generating...' : 'Generate Foundation Certificate'}
                </Button>
                
                <p className="text-xs text-muted-foreground mt-4">
                  Want the full certification? Continue to Phase 5-8 for Advanced Certification.
                </p>
              </div>
            ) : (
              <div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <p className="text-amber-800 font-medium">
                    {eligibility?.reason || 'Complete all requirements to earn your certificate'}
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <BookOpen className="h-5 w-5 text-muted-foreground" />
                    <div className="flex-1">
                      <p className="font-medium">Complete all 23 topics</p>
                      <p className="text-sm text-muted-foreground">All four phases required</p>
                    </div>
                    <Badge variant="secondary">Required</Badge>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <Target className="h-5 w-5 text-muted-foreground" />
                    <div className="flex-1">
                      <p className="font-medium">Pass final certification exam</p>
                      <p className="text-sm text-muted-foreground">Score 85% or higher</p>
                    </div>
                    <Badge variant="secondary">Required</Badge>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <Keyboard className="h-5 w-5 text-muted-foreground" />
                    <div className="flex-1">
                      <p className="font-medium">Pass all 10 typing tests</p>
                      <p className="text-sm text-muted-foreground">Meet WPM and accuracy requirements</p>
                    </div>
                    <Badge variant="secondary">Required</Badge>
                  </div>
                </div>
                
                <div className="mt-6 text-center">
                  <Button variant="outline" onClick={() => navigate('/rpat')}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Return to Training
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default RPATCertificate;
